import api from './api';

export interface DeliveryArea {
    id: string;
    province: string;
    provinceId: number;
    provinceSlug: string;
    district: string;
    districtSlug: string;
    neighborhood: string;
    neighborhoodSlug: string;
    fullName: string;
    searchTerms: string[];
    isActive: boolean;
    deliveryFee: number;
    estimatedDuration: string;
    notes?: string;
    createdAt: any;
    updatedAt: any;
}

export interface Province {
    id: number;
    name: string;
    population: number;
    area: number;
    districts: any[];
}

export interface DistrictSummary {
    name: string;
    total: number;
    active: number;
    inactive: number;
}

export interface GetAreasParams {
    province?: string;
    district?: string;
    isActive?: boolean;
    page?: number;
    limit?: number;
}

export interface DeliveryAreaSearchRebuildSample {
    id: string;
    fullName: string;
    termCount: number;
    aliasPreview: string[];
    customLocationCount: number;
}

export interface DeliveryAreaSearchRebuildResult {
    success: boolean;
    province: string | null;
    processed: number;
    updated: number;
    totalSearchTerms: number;
    averageSearchTermsPerDoc: number;
    samples: DeliveryAreaSearchRebuildSample[];
}

export const deliveryAreaApi = {
    /**
     * Get districts summary for a province
     * @param {string} province - Province name
     * @returns {Promise<{ districts: DistrictSummary[] }>}
     */
    async getDistrictsSummary(province: string): Promise<{ districts: DistrictSummary[] }> {
        try {
            const response = await api.get('/admin/delivery-areas/districts-summary', {
                params: { province }
            });
            return response.data;
        } catch (error: any) {
            console.error('Error fetching districts summary:', error);
            throw new Error(error.response?.data?.error || 'İlçe özeti yüklenemedi');
        }
    },

    /**
     * Get delivery areas with optional filters
     * @param {GetAreasParams} params - Filter parameters
     * @returns {Promise<{ data: DeliveryArea[], total: number, page: number, limit: number }>}
     */
    async getAreas(params?: GetAreasParams): Promise<{ data: DeliveryArea[], total: number, page: number, limit: number }> {
        try {
            const response = await api.get('/admin/delivery-areas/list', { params });
            return response.data;
        } catch (error: any) {
            console.error('Error fetching delivery areas:', error);
            throw new Error(error.response?.data?.error || 'Teslimat alanları yüklenemedi');
        }
    },

    /**
     * Update a single delivery area
     * @param {string} id - Area ID
     * @param {Partial<DeliveryArea>} updates - Updates to apply
     * @returns {Promise<DeliveryArea>}
     */
    async updateArea(id: string, updates: Partial<DeliveryArea>): Promise<DeliveryArea> {
        try {
            const response = await api.put(`/admin/delivery-areas/${id}`, updates);
            return response.data;
        } catch (error: any) {
            console.error('Error updating delivery area:', error);
            throw new Error(error.response?.data?.error || 'Teslimat alanı güncellenemedi');
        }
    },

    /**
     * Bulk update delivery areas by district
     * @param {string} province - Province name
     * @param {string} district - District slug
     * @param {object} updates - Updates to apply to all areas in district
     * @returns {Promise<{ success: boolean, updated: number }>}
     */
    async bulkUpdate(province: string, district: string, updates: any): Promise<{ success: boolean, updated: number }> {
        try {
            const response = await api.post('/admin/delivery-areas/bulk-update', {
                province,
                district,
                updates,
            });
            return response.data;
        } catch (error: any) {
            console.error('Error bulk updating delivery areas:', error);
            throw new Error(error.response?.data?.error || 'Toplu güncelleme başarısız');
        }
    },

    /**
     * Get provinces list from API
     * @returns {Promise<{ provinces: Province[] }>}
     */
    async getProvincesList(): Promise<{ provinces: Province[] }> {
        try {
            const response = await api.get('/admin/delivery-areas/provinces-list');
            return response.data;
        } catch (error: any) {
            console.error('Error fetching provinces list:', error);
            throw new Error(error.response?.data?.error || 'İl listesi yüklenemedi');
        }
    },

    /**
     * Import province data from turkiye-api
     * @param {number} provinceId - Province ID
     * @param {string} provinceName - Province name
     * @param {boolean} force - Force re-import even if data exists
     * @returns {Promise<{ success: boolean, imported: number, skipped: number, message: string }>}
     */
    async importProvinceData(provinceId: number, provinceName: string, force = false): Promise<{ success: boolean, imported: number, skipped: number, message: string }> {
        try {
            const response = await api.post('/admin/delivery-areas/import', {
                provinceId,
                provinceName,
                force,
            });
            return response.data;
        } catch (error: any) {
            console.error('Error importing province data:', error);
            throw new Error(error.response?.data?.error || 'İl verileri import edilemedi');
        }
    },

    async rebuildSearchIndex(province?: string): Promise<DeliveryAreaSearchRebuildResult> {
        try {
            const response = await api.post('/admin/delivery-areas/rebuild-search-index', {
                ...(province ? { province } : {}),
            });
            return response.data;
        } catch (error: any) {
            console.error('Error rebuilding delivery area search index:', error);
            throw new Error(error.response?.data?.error || 'Arama indeksi yeniden oluşturulamadı');
        }
    },

    /**
     * Get custom locations for a neighborhood
     * @param {string} neighborhoodId - Neighborhood ID
     * @returns {Promise<{ locations: any[] }>}
     */
    async getCustomLocations(neighborhoodId: string): Promise<{ locations: any[] }> {
        try {
            const response = await api.get('/admin/delivery-areas/custom-locations', {
                params: { neighborhoodId }
            });
            return response.data;
        } catch (error: any) {
            console.error('Error fetching custom locations:', error);
            throw new Error(error.response?.data?.error || 'Özel lokasyonlar yüklenemedi');
        }
    },

    /**
     * Add custom location (building/street/complex)
     * @param {object} data - Location data
     * @returns {Promise<{ success: boolean, id: string }>}
     */
    async addCustomLocation(data: {
        neighborhoodId: string;
        locationName: string;
        locationType: 'building' | 'street' | 'complex' | 'landmark';
        deliveryFee?: number;
        estimatedDuration?: string;
    }): Promise<{ success: boolean, id: string }> {
        try {
            const response = await api.post('/admin/delivery-areas/custom-locations', data);
            return response.data;
        } catch (error: any) {
            console.error('Error adding custom location:', error);
            throw new Error(error.response?.data?.error || 'Lokasyon eklenemedi');
        }
    },

    /**
     * Update custom location
     * @param {string} id - Location ID
     * @param {object} updates - Fields to update
     * @returns {Promise<{ success: boolean }>}
     */
    async updateCustomLocation(id: string, updates: any): Promise<{ success: boolean }> {
        try {
            const response = await api.put(`/admin/delivery-areas/custom-locations/${id}`, updates);
            return response.data;
        } catch (error: any) {
            console.error('Error updating custom location:', error);
            throw new Error(error.response?.data?.error || 'Lokasyon güncellenemedi');
        }
    },

    /**
     * Delete custom location
     * @param {string} id - Location ID
     * @returns {Promise<{ success: boolean }>}
     */
    async deleteCustomLocation(id: string): Promise<{ success: boolean }> {
        try {
            const response = await api.delete(`/admin/delivery-areas/custom-locations/${id}`);
            return response.data;
        } catch (error: any) {
            console.error('Error deleting custom location:', error);
            throw new Error(error.response?.data?.error || 'Lokasyon silinemedi');
        }
    },

    /**
     * Bulk update selected neighborhoods
     * @param {string[]} neighborhoodIds - Array of neighborhood IDs
     * @param {object} updates - Updates to apply (deliveryFee, isActive, estimatedDuration)
     * @returns {Promise<{ success: boolean, updated: number }>}
     */
    async bulkUpdateNeighborhoods(neighborhoodIds: string[], updates: {
        deliveryFee?: number;
        isActive?: boolean;
        estimatedDuration?: string;
    }): Promise<{ success: boolean, updated: number }> {
        try {
            const response = await api.post('/admin/delivery-areas/bulk-update-neighborhoods', {
                neighborhoodIds,
                updates,
            });
            return response.data;
        } catch (error: any) {
            console.error('Error bulk updating neighborhoods:', error);
            throw new Error(error.response?.data?.error || 'Toplu mahalle güncelleme başarısız');
        }
    },

    /**
     * Bulk update custom locations
     * @param {Array} updates - Array of updates with neighborhoodId, customLocationId, and fields to update
     * @returns {Promise<{ success: boolean, updated: number }>}
     */
    async bulkUpdateCustomLocations(updates: Array<{
        neighborhoodId: string;
        customLocationId: string;
        deliveryFee?: number;
        isActive?: boolean;
        estimatedDuration?: string;
    }>): Promise<{ success: boolean, updated: number }> {
        try {
            const response = await api.post('/admin/delivery-areas/bulk-update-custom-locations', {
                updates,
            });
            return response.data;
        } catch (error: any) {
            console.error('Error bulk updating custom locations:', error);
            throw new Error(error.response?.data?.error || 'Toplu özel konum güncelleme başarısız');
        }
    },

    /**
     * Get all neighborhoods with filtering
     * @param {object} params - Filter parameters
     * @returns {Promise<{ neighborhoods: DeliveryArea[], total: number }>}
     */
    async getAllNeighborhoods(params?: {
        province?: string;
        district?: string;
        minFee?: number;
        maxFee?: number;
        isActive?: boolean;
        limit?: number;
        offset?: number;
    }): Promise<{ neighborhoods: DeliveryArea[], total: number, limit: number, offset: number }> {
        try {
            const response = await api.get('/admin/delivery-areas/all-neighborhoods', { params });
            return response.data;
        } catch (error: any) {
            console.error('Error fetching all neighborhoods:', error);
            throw new Error(error.response?.data?.error || 'Mahalleler yüklenemedi');
        }
    },

    /**
     * Get all custom locations with filtering
     * @param {object} params - Filter parameters
     * @returns {Promise<{ customLocations: any[], total: number }>}
     */
    async getAllCustomLocations(params?: {
        province?: string;
        district?: string;
        neighborhood?: string;
        locationType?: string;
        minFee?: number;
        maxFee?: number;
        isActive?: boolean;
        limit?: number;
        offset?: number;
    }): Promise<{ customLocations: any[], total: number, limit: number, offset: number }> {
        try {
            const response = await api.get('/admin/delivery-areas/all-custom-locations', { params });
            return response.data;
        } catch (error: any) {
            console.error('Error fetching all custom locations:', error);
            throw new Error(error.response?.data?.error || 'Özel konumlar yüklenemedi');
        }
    },
};
